import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAlt from '@mui/icons-material/PersonAddAlt';
import SearchIcon from '@mui/icons-material/Search';
import api from '../api/client';

export default function ManageUsersPage() {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyEmail, setBusyEmail] = useState('');

  const loadUsers = useCallback(async () => {
    const { data } = await api.get('/api/users');
    setUsers(data.users || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadUsers();
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Could not load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadUsers]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) {
      setError('Type at least 2 characters to search Authentik');
      return;
    }
    setError('');
    setNotice('');
    setSearching(true);
    try {
      const { data } = await api.get('/api/users/authentik-search', { params: { q } });
      setResults(data.results || []);
      if (!(data.results || []).length) setNotice('No Authentik users matched that search.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentik search failed');
    } finally {
      setSearching(false);
    }
  };

  const addUser = async (row, role = 'standard') => {
    setBusyEmail(row.email);
    setError('');
    setNotice('');
    try {
      await api.post('/api/users', {
        email: row.email,
        full_name: row.name || '',
        role,
        is_active: true,
      });
      setNotice(`Added ${row.email}`);
      setResults((prev) => prev.map((r) => (r.email === row.email ? { ...r, already_registered: true } : r)));
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || `Could not add ${row.email}`);
    } finally {
      setBusyEmail('');
    }
  };

  const patchUser = async (id, body) => {
    setError('');
    setNotice('');
    try {
      const { data } = await api.patch(`/api/users/${id}`, body);
      setUsers((prev) => prev.map((u) => (u.id === id ? data : u)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
      await loadUsers();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Manage Users</Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '0.9rem' }}>
        Search Authentik, then add people to this app. SSO will not create users automatically.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Find in Authentik</Typography>
        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button type="submit" variant="contained" startIcon={searching ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />} disabled={searching}>
            Search
          </Button>
        </Box>
        {results.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow key={row.uid || row.email}>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.name || row.username}</TableCell>
                    <TableCell>
                      {row.already_registered
                        ? <Chip size="small" label="Already added" color="success" variant="outlined" />
                        : <Chip size="small" label="Not in app" variant="outlined" />}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<PersonAddAlt />}
                        disabled={row.already_registered || busyEmail === row.email}
                        onClick={() => addUser(row)}
                      >
                        Add as standard
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Registered in this app</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>SSO bound</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        value={u.role}
                        onChange={(e) => patchUser(u.id, { role: e.target.value })}
                      >
                        <MenuItem value="standard">standard</MenuItem>
                        <MenuItem value="admin">admin</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!u.is_active}
                      onChange={(e) => patchUser(u.id, { is_active: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell>
                    {u.authentik_bound
                      ? <Chip size="small" color="success" label="Yes" />
                      : <Chip size="small" label="Pending first login" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}